import type { ConsentCategories } from './types';

export type ConsentSignalState = 'granted' | 'denied';

// All seven Consent Mode storage types Google defines
// (developers.google.com/tag-platform/security/guides/consent — "Consent
// types"). Every one is set explicitly in both `default` and `update`: GTM
// treats a consent type that was never set as not-denied, so leaving
// `functionality_storage` / `personalization_storage` out would let any
// future tag gated on them fire without a visitor choice behind it.
export type ConsentSignals = {
  analytics_storage: ConsentSignalState;
  ad_storage: ConsentSignalState;
  ad_user_data: ConsentSignalState;
  ad_personalization: ConsentSignalState;
  functionality_storage: ConsentSignalState;
  personalization_storage: ConsentSignalState;
  security_storage: ConsentSignalState;
};

// Pushed before the GTM container script tag is ever inserted (FR-001).
// `security_storage` and `functionality_storage` are not visitor choices —
// they cover what the site needs to function (session/CSRF cookies, language
// and UI settings) and are always granted, mirroring the `necessary`
// category's `required: true`. `personalization_storage` (content
// recommendations, not ads) has no visitor-facing category, so it is always
// denied — nothing on the site asks for it, and a denied signal is the safe
// side if a tag ever does.
export const DEFAULT_DENIED_CONSENT_SIGNALS: ConsentSignals = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

// Google's documented pattern for a self-hosted/custom CMP: hold GTM's tag
// evaluation open for up to this many ms after `default` so a returning
// visitor's persisted choice (pushed as `update` once ConsentProvider's init
// effect resolves the stored cookie) lands before any tag's fire/block
// decision locks in. Google's own example uses 500.
export const CONSENT_WAIT_FOR_UPDATE_MS = 500;

// Custom-event name pushed after a *visitor-driven* `consent update`
// (accept/reject/save — never the once-per-load push that replays the stored
// cookie). GTM's Custom Event trigger type only matches `{event: '...'}`-
// shaped dataLayer entries; it does not match gtag command entries. A tag
// gated on a one-shot trigger (e.g. Meta Pixel's `_event equals gtm.init`)
// never reconsiders a grant that arrives after it already evaluated —
// `wait_for_update` only covers the initial page-load window. This event
// gives GTM console config a second trigger to attach to such tags ("fire on
// gtm.init OR on this event") so a visitor who grants consent mid-session
// still gets tagged for that same visit.
//
// It is deliberately NOT pushed on the page-load replay of a stored cookie:
// GoogleTagManager's bootstrap pushes that replayed `update` BEFORE
// `gtm.start`, so for a returning consented visitor the gtm.init trigger
// already sees the granted state and fires the tag; also firing this event
// would double-fire every tag carrying the second trigger. Tags given the
// second trigger should still be set to "Once per page" in the console as
// belt-and-braces.
export const CONSENT_UPDATED_EVENT = 'consent_updated';

// Pushed to the dataLayer (as a plain `{event: …}` object, NOT a gtag command)
// immediately after the ad-denying `consent update` when the browser sends
// Global Privacy Control, and always before `gtm.start`. GTM's Custom Event
// trigger type only matches `{event: '…'}`-shaped entries, so this is the
// surface a console trigger or a consent-aware tag can attach to in order to
// know the visitor opted out at the browser level — the `consent update` above
// it carries the enforcement, this carries the signal.
export const GPC_DETECTED_EVENT = 'gpc_detected';

// The GPC ad-denial payload: the three ad_* signals and NOTHING else. Its job
// is an ORDERING guarantee — an ad denial that lands before `gtm.start`, so no
// ad tag can ever evaluate against a region-granted `ad_storage`. It is scoped
// to the ad_* types because ad denial is the part that cannot wait: for a
// RETURNING consented visitor sending GPC this is the only ad denial in the
// batch until their stored decision is replayed one push later.
//
// Since 2026-09-16 it is ALSO what keeps analytics alive on a GPC load: a GPC
// visitor with no cookie is `banner-shown` with no stored decision, so no
// replay follows this push and the region-resolved `analytics_storage` default
// stands. GPC is treated as the sale/share opt-out it is specified to be, not
// as a general opt-out — it denies advertising whatever the region grants, and
// leaves analytics to the regional default and the visitor's own choice. Keep
// this constant to the three ad_* keys: adding `analytics_storage` here would
// silently reinstate the general opt-out.
export const GPC_AD_DENIAL_SIGNALS: Partial<ConsentSignals> = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

// GTM only recognises a dataLayer entry as a gtag command when it is an
// `arguments` object — exactly what Google's `function gtag(){dataLayer.push(
// arguments)}` snippet produces. A plain array `['consent','update',{...}]`
// is silently ignored by gtm.js (verified against the live container: the
// array form never records an `update` in google_tag_data.ics). Every gtag
// command this module emits therefore goes through this helper, which returns
// a real `arguments` object for the given command, so the caller does
// `dataLayer.push(gtagCommand('consent', 'update', signals))`.
export function gtagCommand(...args: unknown[]): IArguments {
  void args;
  // eslint-disable-next-line prefer-rest-params
  return arguments;
}

// `analytics` maps 1:1 to `analytics_storage`. `advertising` maps to all
// three ad_* signals together (FR-003) — there is no finer split; Meta Pixel
// and Google Ads share one visitor-facing category.
//
// GPC-lock helper (spec.md "New Components": gtm-consent-mode.ts owns "the
// GPC-lock helper" alongside the default constant and the mapping function).
// This is defense-in-depth, mirroring the two-place GPC enforcement already
// done in consent-state.ts (init) and CategoryToggles.tsx (UI) — categories
// reaching this function should already have advertising=false whenever gpc
// is true, but this closes the loop at the one place that actually talks to
// Google's tags, so a future bug upstream can't leak a granted ad signal.
export function lockAdvertisingUnderGpc(
  advertising: boolean,
  gpc: boolean,
): boolean {
  return gpc ? false : advertising;
}

export function buildConsentSignals(
  categories: ConsentCategories,
  gpc: boolean,
): ConsentSignals {
  const analyticsState: ConsentSignalState = categories.analytics
    ? 'granted'
    : 'denied';
  const advertisingState: ConsentSignalState = lockAdvertisingUnderGpc(
    categories.advertising,
    gpc,
  )
    ? 'granted'
    : 'denied';
  return {
    analytics_storage: analyticsState,
    ad_storage: advertisingState,
    ad_user_data: advertisingState,
    ad_personalization: advertisingState,
    functionality_storage: 'granted',
    personalization_storage: 'denied',
    security_storage: 'granted',
  };
}
